/*!
 * cueNext.js — next: run another cue when this one finishes
 * © 2025 Rob Canning — GPLv3
 *
 *     text(reveal:poem.txt, next:"zoom(to:star, scale:6, dur:4)")
 *     zoom(to:star, scale:6, dur:4, next:outroCue)
 *
 * A cue that knows when it has finished (text, zoom) hands its next: value
 * here. The value is either
 *   - a cue expression, quoted: next:"zoom(to:a, scale:4, dur:3)" — run as is
 *     (quoted because the parser does not take a bare cue as a parameter
 *     value), or
 *   - the id or uid of an element that carries a cue in data-oscilla or its
 *     id — that element's cue is run, with the element as its target, or
 *   - a list of either, run in order, one straight after another:
 *     next:["ui(uid:[a,b], value:[0.6,0.7])", "ui(uid:[a,b], value:rest)"]
 * A cue inside a quoted cue quotes its own next: with single quotes.
 */

import { handleCueTrigger } from "./cueDispatcher.js";
import { step } from "./sequence.js";

const CUE_EXPR_RE = /^[A-Za-z_][\w-]*\s*\(/;

export function runNext(next, sourceEl = null) {
    if (Array.isArray(next)) {
        let ran = false;
        for (const n of next) ran = runNext(n, sourceEl) || ran;
        return ran;
    }
    if (next == null || next === "") return false;
    const value = String(next).trim().replace(/^["'`]+|["'`]+$/g, "");
    if (!value) return false;

    // held while the sequence is paused (sequence.js)
    if (CUE_EXPR_RE.test(value)) {
        step(() => handleCueTrigger(value, false, true, sourceEl));
        return true;
    }

    const el = document.getElementById(value)
        || document.querySelector(`[data-oscilla-uid="${CSS.escape(value)}"]`);
    const data = el?.getAttribute?.("data-oscilla");
    const expr = (data && CUE_EXPR_RE.test(data)) ? data
        : (el?.id && CUE_EXPR_RE.test(el.id) ? el.id : null);
    if (!expr) {
        console.warn(`[next] "${value}" is neither a cue nor an element carrying one`);
        return false;
    }
    step(() => handleCueTrigger(expr, false, true, el));
    return true;
}
